import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Database, CalendarClock, Network, Workflow, Users, ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DEPARTMENTS } from '../../data/mockData';
import { ORG_TREE, listDivisions } from '../../data/orgTree';
import { useHGS } from '../../context/HGSContext';
import OrgChartPanel from './OrgChartPanel';

const CARDS = [
  {
    id: 'masters', title: 'Master Upload', icon: Database, route: '/admin/masters',
    desc: 'Upload Risk / PCC / Fraud / DLC master sheets · auto-map columns · publish versioned schema.',
    accent: 'from-[#CC0000] to-[#990000] text-white',
  },
  {
    id: 'cycle', title: 'Governance Cycle Setup', icon: CalendarClock, tab: 'cycle',
    desc: 'Define yearly cycle, lock departments, map linked risks, then publish to dispatch tasks.',
    accent: 'from-violet-600 to-violet-700 text-white',
  },
  {
    id: 'departments', title: 'Department Mapping', icon: Network, tab: 'organogram',
    desc: 'Map 6 departments to risks, owners and division reviewers.',
    accent: 'from-sky-600 to-sky-700 text-white',
  },
  {
    id: 'workflow', title: 'Workflow Settings', icon: Workflow, tab: 'modules',
    desc: 'Toggle modules, configure approval flow steps and escalation thresholds.',
    accent: 'from-emerald-600 to-emerald-700 text-white',
  },
  {
    id: 'users', title: 'User Management', icon: Users, tab: 'users',
    desc: 'Provision roles, link users to departments, suspend or rotate access.',
    accent: 'from-amber-600 to-amber-700 text-white',
  },
];

const STAT_TONE = {
  emerald: 'text-emerald-700',
  amber: 'text-amber-700',
  sky: 'text-sky-700',
  slate: 'text-slate-700',
};

export default function AdminHomeTab({ onSwitchTab }) {
  const navigate = useNavigate();
  const { riskPublished, masterMeta } = useHGS();

  const stats = [
    {
      l: 'Active Masters',
      v: Object.values(masterMeta || {}).filter((m) => m.status === 'Active').length,
      tone: 'emerald',
    },
    {
      l: 'Operating Heads',
      v: Object.keys(ORG_TREE).length,
      tone: 'sky',
    },
    { l: 'Divisions', v: listDivisions().length, tone: 'sky' },
    {
      l: 'Departments',
      v: DEPARTMENTS.length,
      tone: 'sky',
    },
    {
      l: 'Cycle Status',
      v: riskPublished?.published ? 'Live' : 'Draft',
      tone: riskPublished?.published ? 'emerald' : 'slate',
    },
  ];

  const onCardClick = (c) => (c.route ? navigate(c.route) : onSwitchTab(c.tab));

  return (
    <div className="space-y-6" data-testid="admin-home">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-base font-bold text-slate-900">Admin Home</h3>
          <p className="text-xs text-slate-500">
            You own the framework. Upload masters, configure the cycle, map departments and publish.
            You do <span className="font-bold text-[#CC0000]">not</span> fill operational responses.
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-bold uppercase">
          <ShieldCheck className="w-3 h-3" /> Framework Owner
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {CARDS.map((c) => (
          <button
            key={c.id}
            data-testid={`admin-card-${c.id}`}
            onClick={() => onCardClick(c)}
            className={cn('group text-left rounded-xl p-5 bg-gradient-to-br shadow-sm hover:shadow-md transition-all flex flex-col', c.accent)}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-lg bg-white/15 backdrop-blur flex items-center justify-center">
                <c.icon className="w-5 h-5" />
              </div>
              <ArrowRight className="w-4 h-4 opacity-60 group-hover:translate-x-0.5 transition" />
            </div>
            <div className="text-base font-bold mb-1.5">{c.title}</div>
            <p className="text-[11px] opacity-90 leading-relaxed flex-1">{c.desc}</p>
            <span className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-bold w-max bg-white/10 hover:bg-white/20 text-white">
              Open <ArrowRight className="w-3 h-3" />
            </span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 pt-2">
        {stats.map((s) => (
          <div key={s.l} className="p-4 rounded-xl bg-white border border-slate-200">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{s.l}</div>
            <div className={cn('text-2xl font-bold mt-1 tabular-nums', STAT_TONE[s.tone])}>{s.v}</div>
          </div>
        ))}
      </div>

      <OrgChartPanel />
    </div>
  );
}
